import React, { useState } from "react";
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { CompanyContact } from "@bindeleddet/core/types";

const { Title } = Typography;

interface Props {
  companyId?: number;
  contact?: CompanyContact;
  handleUpdate?: (contact: CompanyContact) => void;
  handleCreate?: (contact: CompanyContact) => void;
  // When given, the edit form gets a delete button
  handleDelete?: (contact: CompanyContact) => void;
  isOpen: boolean;
  onClose: () => void;
}

const CreateOrEditContact: React.FC<Props> = ({
  companyId,
  contact,
  handleUpdate,
  handleCreate,
  handleDelete,
  isOpen,
  onClose,
}) => {
  const [loading, setLoading] = useState<boolean>(false);

  const publishCompanyContact = (values: CompanyContact) => {
    setLoading(true);
    axios
      .post<CompanyContact>(`/companies/${companyId}/contact-persons/`, {
        ...values,
        company: companyId,
        main_contact: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .then(response => {
        onClose();
        message("success", "Kontaktperson opprettet");
        if (handleCreate) {
          handleCreate(response.data);
        }
        setLoading(false);
      })
      .catch(() => {
        message("error", "Feilmelding. Kontaktperson ikke oppdatert");
        setLoading(false);
      });
  };

  const updateCompanyContact = (values: CompanyContact) => {
    if (!contact) return;
    setLoading(true);
    axios
      .put<CompanyContact>(`/companies/${contact.company}/contact-persons/${contact?.id}/`, {
        ...values,
        updated_at: new Date().toISOString(),
        created_at: contact.created_at,
        main_contact: contact.main_contact,
        company: contact.company,
      })
      .then(response => {
        onClose();
        message("success", "Kontaktperson oppdatert");
        if (handleUpdate) {
          handleUpdate(response.data);
        }
        setLoading(false);
      })
      .catch(() => {
        message("error", "Feilmelding. Kontaktperson ikke oppdatert");
        setLoading(false);
      });
  };

  return (
    <>
      <Modal
        title={<Title level={3}>{!contact ? "Opprett kontaktperson" : "Oppdater kontaktperson"}</Title>}
        centered
        open={isOpen}
        onOk={onClose}
        onCancel={onClose}
        width="70%"
        footer={null}
        bodyStyle={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)", padding: "20px" }}
      >
        <Form layout="vertical" onFinish={contact ? updateCompanyContact : publishCompanyContact}>
          <Form.Item
            name="first_name"
            label="Fornavn"
            rules={[{ required: true, message: "Vennligst skriv inn fornavn" }]}
            initialValue={contact?.first_name}
          >
            <Input placeholder="Skriv inn fornavn..." size="large" />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Etternavn"
            rules={[{ required: true, message: "Vennligst skriv inn etternavn" }]}
            initialValue={contact?.last_name}
          >
            <Input placeholder="Skriv inn etternavn..." size="large" />
          </Form.Item>
          <Form.Item name="position" label="Stilling" initialValue={contact?.position}>
            <Input placeholder="Skriv inn stilling..." size="large" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Epost"
            rules={[{ required: true, message: "Vennligst skriv inn epostadresse" }]}
            initialValue={contact?.email}
          >
            <Input placeholder="Skriv inn epost..." size="large" />
          </Form.Item>
          <Form.Item
            name="telephone"
            label="Telefonnummer"
            rules={[{ required: true, message: "Vennligst skriv inn telefonnummer" }]}
            initialValue={contact?.telephone}
          >
            <InputNumber placeholder="Skriv inn telefonnummer..." size="large" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              size="large"
              style={{ width: "100%", marginTop: "10px" }}
              htmlType="submit"
              loading={loading}
            >
              {contact ? "Oppdater kontaktperson" : "Opprett kontaktperson"}
            </Button>
          </Form.Item>
          {contact && handleDelete && (
            <Popconfirm
              title={`Slett ${contact.first_name} ${contact.last_name}?`}
              okText="Slett"
              okType="danger"
              cancelText="Avbryt"
              onConfirm={() => handleDelete(contact)}
            >
              <Button danger type="text" icon={<DeleteOutlined />} style={{ width: "100%" }}>
                Slett kontaktperson
              </Button>
            </Popconfirm>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CreateOrEditContact;
